import type { Container, Sprite } from 'pixi.js';
import { frac } from '../../shared/scalar';
import { GLOWSTICK } from '../../sim/full/attacks/glowstick';
import type { PlayerState, SimState } from '../../sim/state';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, paintSheet } from '../paint';
import type { PixiPalette } from '../palette';
import { type Look, fade } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import {
  GARLAND,
  GLASSES,
  HAIR,
  HANDS,
  HEAD,
  STICKS,
  SUPERFAN_PIECES,
  UNIT,
  VISOR,
  garlandLight,
} from './superfan-paint';

type Parts = Readonly<Record<keyof typeof SUPERFAN_PIECES, Shape>>;

const EXTENT = 46;
const MIDDLE = 4;
const BEAT_SWELL = { ticks: 2, most: 0.03, calm: 0.3 };
const BLINK = { perBeat: 2, chase: 0.25, sharp: 3, low: 0.55, high: 0.45, calm: 0.75 };
// The halos of the validated sketch, a third wider: our halo is softer than its gradient.
const GLOWS = { light: 6, stick: 14, stickAlpha: 1, glasses: 10, glassesAlpha: 0.9 } as const;
const STICK_GLOW = { along: 4, across: 1.5 } as const;
const FLICK = { ticks: 2, turn: 0.45, reach: 2.5 };
const RAPPEL = { grow: 0.1, calm: 0.3, chase: 2 } as const;

export function stickSide(rank: number): -1 | 1 {
  return rank % 2 === 0 ? 1 : -1;
}

export function stickColor(palette: PixiPalette, side: -1 | 1): number {
  return side === 1 ? palette.healer : palette.mage;
}

function lastThrow(state: SimState, player: PlayerState, beatIndex: number): number {
  let rank: number | undefined;
  let newest = -1;
  for (const prop of state.props ?? []) {
    if (prop.kind === GLOWSTICK && prop.playerId === player.id && prop.id > newest) {
      newest = prop.id;
      rank = prop.rank;
    }
  }
  return rank ?? beatIndex;
}

const LIGHT_TOKENS = ['turquoise', 'mage', 'noyau'] as const;

function createFanLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const outline = add(bodies, parts.torso);
  const torso = add(bodies, parts.torso);
  const lights = Array.from({ length: GARLAND.lights }, () => add(bodies, textures.halo));
  const bulbs = add(bodies, parts.bulbs);
  const stickGlows = [add(glows, textures.halo), add(glows, textures.halo)];
  const sticks = [add(bodies, parts.stick), add(bodies, parts.stick)];
  const hands = [add(bodies, parts.hand), add(bodies, parts.hand)];
  const head = add(bodies, parts.head);
  const hair = add(bodies, parts.hair);
  const glassesGlow = add(glows, textures.halo);
  const glasses = add(bodies, parts.glasses);
  const leds = add(bodies, parts.leds);
  const body = [torso, bulbs, ...sticks, ...hands, head, hair, glasses, leds];
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
    sprite.scale.set(size);
  }

  function shine(sprite: Sprite, along: number, across: number, radius: number, alpha: number) {
    sprite.visible = true;
    put(sprite, along, across);
    sprite.scale.set((radius * UNIT * size) / textures.halo.radius);
    sprite.alpha = alpha;
  }

  return {
    hide() {
      hide(...body, outline, ...lights, ...stickGlows, glassesGlow);
    },
    place(input) {
      const { player, state, x, y, angle, scale, color, frame } = input;
      const { now, calm, palette, light, tempo } = frame;
      const beats = now / tempo.ticksPerBeat;
      const call = calling(input.sinceSkill, input.sinceBeat, tempo.ticksPerBeat);
      const swell =
        fade(input.sinceBeat, BEAT_SWELL.ticks) * BEAT_SWELL.most * (calm ? BEAT_SWELL.calm : 1);
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale * (1 + swell) * (1 + RAPPEL.grow * (calm ? RAPPEL.calm : 1) * call);
      facing = angle;
      for (const sprite of body) {
        sprite.visible = true;
      }

      put(torso, 0, 0);
      setTint(torso, color);
      placeOutline(outline, torso, parts.torso.texture, REFERENCE_RADIUS, frame);

      const chase = (call > 0 && !calm ? RAPPEL.chase : 1) * BLINK.perBeat * beats;
      lights.forEach((glow, index) => {
        const { along, across } = garlandLight(index);
        const flare = calm
          ? BLINK.calm
          : BLINK.low + BLINK.high * blink(chase + index * BLINK.chase);
        shine(glow, along, across, GLOWS.light, flare * light.haloAlpha);
        glow.blendMode = light.additive ? 'add' : 'normal';
        setTint(glow, palette[LIGHT_TOKENS[index % LIGHT_TOKENS.length] ?? 'turquoise']);
      });
      put(bulbs, 0, 0);
      setTint(bulbs, 0xffffff);

      const thrower = stickSide(lastThrow(state, player, input.beatIndex));
      const flick = input.fireCount > 0 ? fade(input.sinceFire, FLICK.ticks) : 0;
      for (const [index, side] of [-1, 1].entries()) {
        const stick = sticks[index];
        const glow = stickGlows[index];
        const hand = hands[index];
        if (stick === undefined || glow === undefined || hand === undefined) {
          continue;
        }
        const thrown = side === thrower ? flick : 0;
        const tilt = side * STICKS.turn * (1 - thrown);
        const along = STICKS.along + FLICK.reach * thrown;
        const across = side * STICKS.across;
        const tint = stickColor(palette, side === 1 ? 1 : -1);
        put(
          stick,
          along + Math.cos(tilt) * STICKS.middle,
          across + Math.sin(tilt) * STICKS.middle,
          tilt,
        );
        setTint(stick, tint);
        shine(
          glow,
          along + Math.cos(tilt) * STICK_GLOW.along - Math.sin(tilt) * side * STICK_GLOW.across,
          across + Math.sin(tilt) * STICK_GLOW.along + Math.cos(tilt) * side * STICK_GLOW.across,
          GLOWS.stick,
          Math.min(1, GLOWS.stickAlpha * light.haloAlpha * (1 + thrown)),
        );
        setTint(glow, tint);
        put(hand, HANDS.along + FLICK.reach * thrown, side * HANDS.across);
        setTint(hand, input.teint);
      }

      put(head, HEAD.along, 0);
      setTint(head, input.teint);
      put(hair, HEAD.hair, 0);
      setTint(hair, HAIR);
      shine(glassesGlow, GLASSES.along, 0, GLOWS.glasses, GLOWS.glassesAlpha * light.haloAlpha);
      setTint(glassesGlow, palette.turquoise);
      put(glasses, GLASSES.along, 0);
      setTint(glasses, VISOR);
      put(leds, GLASSES.along, 0);
      setTint(leds, palette.turquoise);
      return 0;
    },
  };
}

function calling(sinceSkill: number, sinceBeat: number, ticksPerBeat: number): number {
  if (sinceSkill < 0 || sinceBeat < sinceSkill) {
    return 0;
  }
  return Math.min(1, sinceSkill / Math.max(1e-6, sinceSkill + ticksPerBeat - sinceBeat));
}

function blink(at: number): number {
  return Math.exp(-BLINK.sharp * frac(at));
}

export const SUPERFAN_DRAWING: ClassDrawing = {
  classId: 'superfan',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(SUPERFAN_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createFanLook(bodies, glows, textures, shapes),
    };
  },
};
